import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { validateNeoInput } from '../utils/neoGuardrails';
import './NeoChatbot.css';

export default function NeoChatbot({ activeDimension = null, dimensions = [], sessionId = null, showToggleButton = true, isOpen: controlledIsOpen = null, onOpenChange = null }) {
  // Initialize mobile detection immediately
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(() => {
    if (typeof window !== 'undefined') {
      const isMobile = window.innerWidth <= 1024;
      console.log('[NeoChatbot] Initial screen width:', window.innerWidth, 'isMobile:', isMobile);
      return isMobile;
    }
    return false;
  });
  
  // On mobile/tablet, always start closed
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const internalIsOpenRef = useRef(internalIsOpen);
  const isOpen = controlledIsOpen !== null ? controlledIsOpen : internalIsOpen;
  const setIsOpen = (value) => {
    if (onOpenChange) {
      onOpenChange(value);
    } else {
      setInternalIsOpen(value);
    }
  };
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const panelRef = useRef(null);
  const floatingIconRef = useRef(null);
  const [showHelpPopup, setShowHelpPopup] = useState(false);

  useEffect(() => {
    internalIsOpenRef.current = internalIsOpen;
  }, [internalIsOpen]);

  // Detect if device is tablet or mobile and handle resize
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobile = window.innerWidth <= 1024;
      console.log('[NeoChatbot] Screen resize detected:', window.innerWidth, 'isMobile:', isMobile);
      setIsMobileOrTablet(isMobile);
      
      // On tablet/mobile, ensure chat is closed if it was open
      if (isMobile) {
        const effectiveIsOpen = controlledIsOpen !== null ? controlledIsOpen : internalIsOpenRef.current;
        if (controlledIsOpen === null && effectiveIsOpen) {
          console.log('[NeoChatbot] Closing chat on mobile (internal state)');
          setInternalIsOpen(false);
        } else if (controlledIsOpen && onOpenChange) {
          console.log('[NeoChatbot] Closing chat on mobile (external state)');
          onOpenChange(false);
        }
      }
    };
    
    // Check immediately
    checkScreenSize();
    
    // Listen for resize
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [controlledIsOpen, onOpenChange]);

  // Click outside to close (tablet/mobile only)
  useEffect(() => {
    if (!isMobileOrTablet || !isOpen) return;

    const handleClickOutside = (event) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        floatingIconRef.current &&
        !floatingIconRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobileOrTablet, isOpen, setIsOpen]);

  // Show intro message when chatbot opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const introMessage = {
        type: 'neo',
        text: sessionId 
          ? `👋 Hi! I'm NEO, your GenAI Maturity Advisor!

I have access to your assessment report and can answer questions about your specific results, recommendations, and maturity levels.

You can ask me about:
- Your current maturity levels for each dimension
- Specific recommendations from your report
- How to improve in particular areas
- Best practices and KPIs for your situation
- Moving to the next level in any dimension

What would you like to know about your report? 👇`
          : `👋 Hi, I'm NEO — your GenAI Maturity Advisor!

I can help you understand what each maturity level means, how to move to the next level, and what actions or KPIs to focus on.

You can ask me about any of the 7 dimensions — Code, Content, Cloud, Conversations, Data, People, and Responsible AI — and I'll guide you with insights and next steps.

Try one of the sample questions below to get started 👇`
      };
      setMessages([introMessage]);
      // Scroll to top to show welcome message when chatbot first opens
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = 0;
      }
    }
  }, [isOpen, sessionId]);

  // Scroll to bottom only when user sends a message, not when Neo responds
  // This keeps the user's question visible and doesn't auto-scroll to bottom when Neo answers
  useEffect(() => {
    // Only auto-scroll if the last message is from the user (not from Neo)
    // This way, when user asks a question, we scroll to show their question
    // But when Neo answers, we stay at the top of the answer (no auto-scroll)
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.type === 'user' && chatEndRef.current) {
        // Only scroll when user sends a message, not when Neo responds
        chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages]);

  const starterPrompts = [
    { text: 'What does Level 3 mean for the Code dimension?', category: '🧠 Understanding Levels' },
    { text: "What's the best way to scale GenAI across teams?", category: '🏗️ Enterprise Adoption' },
    { text: 'Which tools can help us improve Cloud maturity?', category: '🔧 Tools & Best Practices' },
    { text: 'How do we ensure ethical and explainable AI usage?', category: '🧭 Responsible AI' },
    { text: 'How can we move from Level 1 to Level 2 for Data?', category: '🚀 Improvement Path' }
  ];

  const handleSendMessage = async (question) => {
    if (!question || question.trim().length === 0) return;

    // Validate input with guardrails
    const validation = validateNeoInput(question);
    if (!validation.valid) {
      const errorMessage = {
        type: 'neo',
        text: `⚠️ ${validation.error}`
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    const sanitizedQuestion = validation.sanitized;
    const userMessage = {
      type: 'user',
      text: sanitizedQuestion
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const data = await api.askNeo(sanitizedQuestion, activeDimension, sessionId);

      // Handle response - show answer even if success: false (it may contain helpful error message)
      if (data.answer) {
        const neoMessage = {
          type: 'neo',
          text: data.answer
        };
        setMessages(prev => [...prev, neoMessage]);
      } else if (data.success === false) {
        // If no answer but success: false, treat as error
        throw new Error(data.error || 'Failed to get response');
      } else {
        // Unexpected response format
        throw new Error('Unexpected response format from server');
      }
    } catch (error) {
      console.error('NEO Chat error:', error);
      let errorText = 'I apologize, but I encountered an error. Please try again.';
      
      // Provide more specific error messages
      if (error.message) {
        // Check if server returned the friendly error message
        if (error.message.includes('Something went wrong. It\'s not you, It\'s us.') || 
            error.message.includes('Payment Required') || 
            error.message.includes('402')) {
          errorText = 'Something went wrong. It\'s not you, It\'s us.';
        } else if (error.message.includes('401') || error.message.includes('Authentication')) {
          errorText = 'I apologize, but there is an authentication issue with the AI service. Please contact your administrator.';
        } else if (error.message.includes('timeout')) {
          errorText = 'The request took too long. Please try asking a simpler question.';
        } else if (error.message.includes('Failed to fetch') || error.message.includes('Network')) {
          errorText = 'Network error. Please check your connection and try again.';
        } else {
          // Include the actual error in console but show user-friendly message
          errorText = `I apologize, but I encountered an error: ${error.message}. Please try again or rephrase your question.`;
        }
      }
      
      const errorMessage = {
        type: 'neo',
        text: errorText
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStarterPromptClick = (prompt) => {
    handleSendMessage(prompt);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading) {
      handleSendMessage(inputValue.trim());
    }
  };

  const showFloatingIcon = isMobileOrTablet && !isOpen;

  const handleFloatingClick = () => {
    setShowHelpPopup(false);
    setIsOpen(true);
  };

  return (
    <>
      {/* Floating NEO Icon - Always render, CSS + class controls visibility */}
      <div className={`neo-floating-wrapper ${showFloatingIcon ? 'neo-floating-visible' : ''}`}>
        <div className={`neo-help-popup ${showHelpPopup ? 'open' : ''}`}>
          <div className="neo-help-text">
            Hey there 👋 If you need any assistance, I’m always here.
          </div>
        </div>
      </div>

      {/* Toggle Button - Desktop only (only show if showToggleButton is true and not mobile/tablet) */}
      {showToggleButton && !isMobileOrTablet && (
        <div
          id="neo-toggle"
          className={`neo-toggle ${isOpen ? 'hidden' : ''}`}
          onClick={() => setIsOpen(true)}
        >
          💬
        </div>
      )}

      {/* Chat Panel */}
      <div
        ref={panelRef}
        id="neo-panel"
        className={`neo-panel ${isOpen ? '' : 'hidden'} ${isMobileOrTablet ? 'neo-panel-mobile' : ''}`}
      >
        {/* Header */}
        <div className="neo-header">
          <span className="neo-header-title">NEO Advisor</span>
          <button
            id="neo-close"
            className="neo-close"
            onClick={() => setIsOpen(false)}
          >
            ×
          </button>
        </div>

        {/* Chat Messages */}
        <div
          id="neo-chat"
          ref={chatContainerRef}
          className="neo-chat"
        >
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`neo-message ${msg.type === 'user' ? 'neo-message-user' : 'neo-message-neo'}`}
            >
              {msg.text}
            </div>
          ))}

          {/* Starter Prompts (show only in intro) */}
          {messages.length === 1 && messages[0].type === 'neo' && (
            <div className="neo-starter-prompts">
              {starterPrompts.map((prompt, idx) => (
                <div
                  key={idx}
                  className="neo-starter-prompt"
                  onClick={() => handleStarterPromptClick(prompt.text)}
                >
                  <span className="neo-prompt-category">{prompt.category}</span>
                  <span className="neo-prompt-text">{prompt.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Loading indicator */}
          {isLoading && (
            <div className="neo-message neo-message-neo">
              <div className="neo-typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* Input Form */}
        <div className="neo-input-container">
          <form onSubmit={handleSubmit} className="neo-input-form">
            {inputValue.length > 800 && (
              <div className={`neo-input-char-count ${inputValue.length >= 1000 ? 'error' : inputValue.length >= 950 ? 'warning' : ''}`}>
                {inputValue.length}/1000
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
              <input
                id="neo-input"
                type="text"
                placeholder={activeDimension ? `Ask NEO about ${dimensions.find(d => d.id === activeDimension)?.title || 'this dimension'}...` : 'Ask NEO about dimensions or levels...'}
                className="neo-input"
                value={inputValue}
                onChange={(e) => {
                  const value = e.target.value;
                  // Enforce max length
                  if (value.length <= 1000) {
                    setInputValue(value);
                  }
                }}
                disabled={isLoading}
                maxLength={1000}
                style={{ flex: 1 }}
              />
              <button
                id="neo-send"
                type="submit"
                className="neo-send-btn"
                disabled={isLoading || !inputValue.trim()}
              >
                Send
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

